import {FieldRequiredLabel, InfoTooltip, Tabs, TabsContent} from '@sollapay/ui/components';
import {useCallback, useEffect, useRef, useState} from 'react';

import {FileUploadManager} from './FileUploadManager';

import type {DocumentContext, DocumentEntityType, DocumentType} from '@sollapay/enums';
import type {DocumentMetadata} from '@sollapay/types';
import type {IconName, TabItem} from '@sollapay/ui/components';
import type {ReactNode} from 'react';

const ICON_UPLOADED: IconName = 'check-circle-dashed';
const ICON_PENDING: IconName = 'upload';
const ICON_ERROR: IconName = 'alert-circle';

export interface DocumentUploadTab {
  value: string;
  label: TabItem['label'];
  documentType: DocumentType;
  /** Shows the required asterisk — display only; enforcement is the caller's `error`. */
  required?: boolean;
  /** Maximum number of files allowed for this document type */
  maxFiles?: number;
  /** Externally-owned error (e.g. required with nothing uploaded, gated on `showErrors` by the
   * caller) — drives the red border on the upload zone and the tab's own error icon. */
  error?: string;
  /** Info icon shown next to the tab's label — e.g. clarifying when an upload can be skipped. */
  tooltip?: ReactNode;
}

export interface DocumentUploadTabsProps {
  tabs: DocumentUploadTab[];
  entityType: DocumentEntityType;
  entityId: string;
  documentContext: DocumentContext;
  metadata?: DocumentMetadata;
  metadataFilter?: Record<string, string | undefined>;
  skipProcessing?: boolean;
  value?: string;
  defaultValue?: string;
  /** When true, hides the upload dropzone and delete affordance — documents become view/download only. */
  readOnly?: boolean;
  onValueChange?: (value: string) => void;
  onDocumentChange?: () => void;
  onDocumentUploaded?: (documentId: string) => void;
  /** Called whenever the document list changes — receives the document IDs of the current tabs. */
  onDocumentsLoaded?: (documentIds: string[]) => void;
  className?: string;
  children?: ReactNode;
}

export function DocumentUploadTabs({
  tabs,
  entityType,
  entityId,
  documentContext,
  metadata,
  metadataFilter,
  skipProcessing,
  value,
  defaultValue,
  readOnly,
  onValueChange,
  onDocumentChange,
  onDocumentUploaded,
  onDocumentsLoaded,
  className,
  children
}: DocumentUploadTabsProps) {
  const [tabDocIds, setTabDocIds] = useState<Record<string, string[]>>({});
  const [tabErrors, setTabErrors] = useState<Record<string, boolean>>({});

  const [selectedValue, setSelectedValue] = useState(defaultValue ?? tabs[0]?.value);
  // A caller can swap the tab list (e.g. ID type toggled) — fall back to the first tab when the selection is gone.
  const activeValue =
    value ?? (tabs.some(tab => tab.value === selectedValue) ? selectedValue : tabs[0]?.value);

  const onDocumentsLoadedRef = useRef(onDocumentsLoaded);
  onDocumentsLoadedRef.current = onDocumentsLoaded;

  const tabValuesKey = tabs.map(tab => tab.value).join('|');
  useEffect(() => {
    const currentIds = tabValuesKey.split('|').flatMap(tabValue => tabDocIds[tabValue] ?? []);
    onDocumentsLoadedRef.current?.(currentIds);
  }, [tabDocIds, tabValuesKey]);

  const handleTabDocumentsLoaded = useCallback((tabValue: string, documentIds: string[]) => {
    setTabDocIds(prev => {
      const existing = prev[tabValue];
      if (
        existing &&
        existing.length === documentIds.length &&
        existing.every((id, i) => id === documentIds[i])
      ) {
        return prev;
      }
      return {...prev, [tabValue]: documentIds};
    });
  }, []);

  const handleTabErrorChange = useCallback((tabValue: string, hasError: boolean) => {
    setTabErrors(prev => (prev[tabValue] === hasError ? prev : {...prev, [tabValue]: hasError}));
  }, []);

  const tabItems: TabItem[] = tabs.map(tab => {
    const hasUploaded = (tabDocIds[tab.value]?.length ?? 0) > 0;
    const hasError = (tabErrors[tab.value] ?? false) || !!tab.error;
    return {
      value: tab.value,
      label: (
        <span className="inline-flex items-center gap-1">
          <FieldRequiredLabel required={tab.required}>{tab.label}</FieldRequiredLabel>
          {tab.tooltip && <InfoTooltip content={tab.tooltip} />}
        </span>
      ),
      icon: hasError ? ICON_ERROR : hasUploaded ? ICON_UPLOADED : ICON_PENDING,
      iconClassName: hasError ? 'text-text-error' : undefined
    };
  });

  return (
    <Tabs
      tabs={tabItems}
      variant="secondary"
      value={activeValue}
      onValueChange={nextValue => {
        setSelectedValue(nextValue);
        onValueChange?.(nextValue);
      }}
      className={className}
    >
      {tabs.map(tab => (
        <TabsContent key={tab.value} value={tab.value} keepMounted>
          <FileUploadManager
            entityType={entityType}
            entityId={entityId}
            documentContext={documentContext}
            documentType={tab.documentType}
            metadata={metadata}
            metadataFilter={metadataFilter}
            skipProcessing={skipProcessing}
            maxFiles={tab.maxFiles}
            error={tab.error}
            readOnly={readOnly}
            onDocumentChange={onDocumentChange}
            onDocumentUploaded={onDocumentUploaded}
            onDocumentsLoaded={ids => handleTabDocumentsLoaded(tab.value, ids)}
            onUploadErrorChange={hasError => handleTabErrorChange(tab.value, hasError)}
          />
        </TabsContent>
      ))}
      {children}
    </Tabs>
  );
}
